import { Component, Input, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  BaseMapType,
  ISettingKeyValuePair,
  SettingsService,
  WeatherLayerType
} from 'src/app/configuration/settings.service';
import { filterSelectorFadeDown } from 'src/app/shared/animations/animations';
import { ConfigurationModel, FeatureFlagEnum, MapLayer } from 'src/app/shared/models/configuration.model';
import { ConfigurationService } from '../../../configuration/configuration.service';
import { MapLayersManager } from '../map-viewer/map-layers-manager';
import { environment } from '../../../../environments/environment';
import { MapControlService } from '../map-viewer/services/map-control.service';
import { MatDialog } from '@angular/material/dialog';
import { NgClass, NgStyle } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { CustomLayersVisibilitySettingsComponent } from './custom-layers-visibility-settings/custom-layers-visibility-settings.component';

@Component({
  selector: 'app-map-layer-switcher',
  templateUrl: './map-layer-switcher.component.html',
  styleUrls: ['./map-layer-switcher.component.scss'],
  animations: [filterSelectorFadeDown],
  imports: [NgStyle, MatIcon, NgClass, MatIconButton, MatTooltip],
})
export class MapLayerSwitcherComponent implements OnInit, OnDestroy {
  @Input() mapLayersManager: MapLayersManager;

  filterOpen = false;

  showRadar = false;
  showWarnings = false;
  showTraffic = false;
  baseMapLayerType: string;
  customBaseMapLayerId: string;
  isOverflowing = false;

  configuration: ConfigurationModel;
  customMapLayers: MapLayer[];
  customBaseMapLayers: MapLayer[];
  visibleCustomLayerIdMap = new Map<number, boolean>();

  baseMaps: BaseMapType[] = [BaseMapType.WINTER, BaseMapType.DARK, BaseMapType.IMAGERY, BaseMapType.OUTDOORS];

  // this is a hack to access enums from templates
  BaseMapType = BaseMapType;
  FeatureFlagEnum = FeatureFlagEnum;
  WeatherLayerType = WeatherLayerType;

  private readonly openSubscriptions = Array<Subscription>();
  private wheelListener?: (e: WheelEvent) => void;
  private wheelTimeout?: ReturnType<typeof setTimeout>;

  @ViewChild('horizontallyScrollableBox') boxRef!: ElementRef;

  constructor(
    private settingsService: SettingsService,
    private configurationService: ConfigurationService,
    private mapControlService: MapControlService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    const configurationSubscription = this.configurationService.sharedConfigurationModel.subscribe(configuration => {
      this.configuration = configuration;
      if (this.configuration) {
        this.setCustomMapLayers();
      }
    });
    this.openSubscriptions.push(configurationSubscription);

    const settingsSubscription = this.settingsService.settingsChangedObservable.subscribe((keyValuePair: ISettingKeyValuePair) => {
      this.onSettingsChanged(keyValuePair.key, keyValuePair.value);
    });
    this.openSubscriptions.push(settingsSubscription);

    this.mapControlService.mapLayerSwitcher$.subscribe(state => {
      this.filterOpen = state;

      clearTimeout(this.wheelTimeout);
      if (this.filterOpen) {
        // force horizontal scroll by mouse wheel
        this.wheelTimeout = setTimeout(() => {
          const box = this.boxRef?.nativeElement;
          if (!box) {
            return;
          }

          if (this.wheelListener) {
            box.removeEventListener('wheel', this.wheelListener);
            this.wheelListener = undefined;
          }

          this.wheelListener = (e: WheelEvent) => {
            // use deltaX for horizontal (Apple Magic Mouse or touchpad), deltaY for vertical (standard mouse wheel)
            box.scrollLeft += e.deltaX !== 0 ? e.deltaX : e.deltaY;
          };
          box.addEventListener('wheel', this.wheelListener, { passive: true });

          // show the tooltip if the content is too wide
          this.isOverflowing = box.scrollWidth > box.clientWidth;
        }, 300);
      }
    });
  }

  hasFeatureFlag(featureFlag: string): boolean {
    return this.configuration.featureFlags.find(value => value.isEnabled && value.name === featureFlag) !== undefined;
  }

  toggleVisibility() {
    this.mapControlService.toggleMapLayerSwitcher(!this.filterOpen);
  }

  switchBaseMap(baseMapType: BaseMapType) {
    this.settingsService.setStringValue(SettingsService.BASE_MAP_LAYER_KEY, baseMapType);
  }

  switchToCustomBaseMap(layer: MapLayer) {
    this.settingsService.setStringValue(SettingsService.CUSTOM_BASE_MAP_KEY, layer.name);
    this.settingsService.setStringValue(SettingsService.BASE_MAP_LAYER_KEY, BaseMapType.CUSTOM);
  }

  toggleRadar() {
    this.toggleCustomLayer(SettingsService.WEATHER_RADAR_LAYER_KEY, !this.showRadar);
    if (!this.showRadar) {
      this.toggleCustomLayer(SettingsService.WEATHER_WARNINGS_LAYER_KEY, false);
    }
  }

  toggleTrafficLayer() {
    this.toggleCustomLayer(SettingsService.TRAFFIC_LAYER_KEY, !this.showTraffic);
  }

  toggleCustomLayer(key: string, checked: boolean) {
    this.settingsService.setBooleanValue(key, checked);
  }

  private onSettingsChanged(key: string, value: any) {
    switch (key) {
      case SettingsService.BASE_MAP_LAYER_KEY:
        this.baseMapLayerType = value;
        break;
      case SettingsService.CUSTOM_BASE_MAP_KEY:
        this.customBaseMapLayerId = value;
        break;
      case SettingsService.WEATHER_RADAR_LAYER_KEY:
        this.showRadar = value;
        break;
      case SettingsService.WEATHER_WARNINGS_LAYER_KEY:
        this.showWarnings = value;
        break;
      case SettingsService.TRAFFIC_LAYER_KEY:
        this.showTraffic = value;
        break;
      default: // other layers in configuration
        const mapLayer = this.customMapLayers.find(it => it.name === key);
        if (mapLayer !== undefined) {
          mapLayer.checked = value as boolean;
        }
        break;
    }
  }

  ngOnDestroy() {
    this.openSubscriptions.forEach(subscription => subscription?.unsubscribe());
    const box = this.boxRef?.nativeElement;
    clearTimeout(this.wheelTimeout);
    if (box && this.wheelListener) {
      box.removeEventListener('wheel', this.wheelListener);
    }
  }

  baseMapTypeBackground(baseMapType: BaseMapType) {
    // if class defined in SCSS then there is a problem with base_href on AWS env
    // this is a workaround where JS knows about base_href
    const basePath = environment.base_href;
    switch (baseMapType) {
      case BaseMapType.OUTDOORS:
        return {
          'background-image': `url(\'${basePath}assets/map_thumbnails/outdoor_thumb.png\')`,
        };
      case BaseMapType.DARK:
        return {
          'background-image': `url(\'${basePath}assets/map_thumbnails/darkgrey_thumb.png\')`,
        };
      case BaseMapType.IMAGERY:
        return {
          'background-image': `url(\'${basePath}assets/map_thumbnails/satellite_thumb.png\')`,
        };
      case BaseMapType.WINTER:
        return {
          'background-image': `url(\'${basePath}assets/map_thumbnails/winter_thumb.png\')`,
        };
      default: {
        return {};
      }
    }
  }

  weatherLayerToggleBackground(weatherLayerName: string) {
    const basePath = environment.base_href;
    switch (weatherLayerName) {
      case WeatherLayerType.RADAR:
        return {
          'background-image': `url(\'${basePath}assets/map_thumbnails/radar_thumb.png\')`,
        };
      case WeatherLayerType.WARNINGS:
        return {
          'background-image': `url(\'${basePath}assets/map_thumbnails/warnings_thumb.png\')`,
        };
      case WeatherLayerType.TRAFFIC:
        return {
          'background-image': `url(\'${basePath}assets/map_thumbnails/traffic_thumb.png\')`,
        };
      default:
        return {};
    }
  }

  toggleCustomMapLayer(customLayer: MapLayer, enable: boolean) {
    this.settingsService.setBooleanValue(customLayer.name, enable);
  }

  private setCustomMapLayers() {
    // load visible custom map layers from user browser storage settings
    this.visibleCustomLayerIdMap.clear();
    const visibleLayersFromSettings = this.settingsService.getStringValue(SettingsService.VISIBLE_CUSTOM_MAP_LAYERS);
    (visibleLayersFromSettings && visibleLayersFromSettings.length > 0 ? visibleLayersFromSettings : '')
      .split(':::')
      .map(layerId => +layerId)
      .forEach(layerId => this.visibleCustomLayerIdMap.set(layerId, true));

    // show in switcher component only the user enabled ones
    this.customMapLayers = this.configuration.additionalLayers.filter(l => !l.useAsBaseMap && !!this.visibleCustomLayerIdMap.get(l.id));
    this.customBaseMapLayers = this.configuration.additionalLayers.filter(l => l.useAsBaseMap && !!this.visibleCustomLayerIdMap.get(l.id));
    this.customMapLayers.forEach(layer => {
      layer.checked = !!this.settingsService.getBooleanValue(layer.name);
    });
  }

  openLayerVisibilitySettings() {
    this.dialog
      .open(CustomLayersVisibilitySettingsComponent, {
        minWidth: '60vw',
        maxWidth: '80vw',
        maxHeight: '85vh',
        width: '750px',
        height: '700px',
        data: null,
      })
      .afterClosed()
      .subscribe(() => {
        // re-read the settings which might change
        this.setCustomMapLayers();
      });
  }
}
